import { PROTOCOLS, PEER_CONNECTION_STATUS, WEBRTC_RELIABLE_CHANNEL, WEBRTC_UNRELIABLE_CHANNEL } from "@gotti/core";
import { BasePeerConnection } from "../BasePeerConnection.js";

if(typeof window !== 'undefined') {
    import('webrtc-adapter');
}

export enum SocketType {
    UDP,
    TCP
}

export interface PeerConnectionConfig {
    iceServers?: Array<RTCIceServer>,
    socketType?: SocketType,
    timeout?: number,
    retries?: number,
    retryTimeout?: number,
}

export class ServerPeerConnection extends BasePeerConnection {
  public status : PEER_CONNECTION_STATUS = PEER_CONNECTION_STATUS.NONE;
  connected: boolean = false;
  onPeerMessageHandler: (label: string, data: any) => void;
  /** Fired when the reliable data channel to the server drops while connected
   *  (the WebRTC transport's connection-loss signal — the signaling ws closes
   *  normally after the handshake, so it is NOT the right death signal). */
  onServerDisconnected?: () => void;
  private _onConnected : () => void
  private reliableChannelLabel?: string;
  private unreliableChannelLabel?: string;

    constructor(
      privateId: string,
      configOptions: PeerConnectionConfig,
      readonly ws: WebSocket,
    ) {
        // create unique channel id for players by ordering by index then joining
      super({
        channelName: privateId,
        ...configOptions,
      })  

    }
  
    onIceCandidate(event: { candidate?: RTCIceCandidate; }): void {
      if(event.candidate) {
        // console.log('SEND UP PEER SIGNAL REQUEST')
        this.ws.send(JSON.stringify([PROTOCOLS.PEER_SIGNAL_REQUEST, { candidate: event.candidate }]));
      }
    }


    public onConnected(cb: (opts?: any) => void) {
      this._onConnected = cb;
    }
    public offConnected() {
        delete this._onConnected;
    }
  async onDataChannelOpen(label: string) {
    if(label === WEBRTC_RELIABLE_CHANNEL) {
      this.reliableChannelLabel = label;
      if(!this.connected) {
        this.connected = true;
        this._onConnected?.();
      }
      return;
    }
    if(label === WEBRTC_UNRELIABLE_CHANNEL) {
      this.unreliableChannelLabel = label;
    }
    }

    onLocalDescription(event: { sdp: RTCLocalSessionDescriptionInit; isInitial?: boolean, requestOptions?: any }): void {
      if(event.isInitial) {
        throw new Error(`Should not be initial...`)
      }
      this.ws.send(JSON.stringify([PROTOCOLS.PEER_SIGNAL_REQUEST, { sdp: event.sdp}]))
    }

    onConnectionClose(label: string) {
      if(label === WEBRTC_RELIABLE_CHANNEL && this.connected) {
         this.connected = false;
         this.onServerDisconnected?.();
      }
    }

    onPeerMessage(label: string, data: any) {
      this.onPeerMessageHandler?.(label, data);
    }

    public sendReliable(message: string | ArrayBuffer) {
      if(!this.reliableChannelLabel) {
        throw new Error('Reliable channel not ready');
      }
      super.send(message, this.reliableChannelLabel);
    }

    public sendUnreliable(message: string | ArrayBuffer) {
      const label = this.unreliableChannelLabel ?? this.reliableChannelLabel;
      if(!label) {
        throw new Error('No data channel available');
      }
      super.send(message, label);
    }
}